import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

// Remove /api/ path from URL for Socket.io connection
// Use runtime environment detection for seamless localhost-to-production
const getSocketUrl = () => {
  // Check if we're in production/staging
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    // Production/Staging: Use the same host as the current page
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    return `${protocol}//${window.location.hostname}`;
  }

  // Localhost development: Direct connection to backend
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  // Strip trailing /api or /api/ from the URL
  return apiUrl.replace(/\/api\/?$/, '');
};

const SOCKET_URL = getSocketUrl();

interface UseSocketOptions {
  userId?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onNewMessage?: (message: any) => void;
}

export const useSocket = (options: UseSocketOptions = {}) => {
  const { userId, onConnect, onDisconnect, onNewMessage } = options;
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    console.log('🔌 [SOCKET] Initializing socket connection to', SOCKET_URL);

    // Initialize socket connection
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('🔌 [SOCKET] Connected:', socket.id);
      setIsConnected(true);

      // Join user's personal room if userId is provided
      if (userId) {
        console.log('🔌 [SOCKET] Joining user room:', userId);
        socket.emit('join', userId);
      }

      onConnect?.();
    });

    socket.on('disconnect', () => {
      console.log('🔌 [SOCKET] Disconnected');
      setIsConnected(false);
      onDisconnect?.();
    });

    socket.on('connect_error', (error) => {
      console.error('🔌 [SOCKET] Connection error:', error);
    });

    // Listen for new messages
    socket.on('new-message', (message) => {
      console.log('🔌 [SOCKET] Received new message:', message);
      onNewMessage?.(message);
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 [SOCKET] Cleaning up socket connection');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('new-message');
      socket.disconnect();
    };
  }, [userId, onConnect, onDisconnect, onNewMessage]);

  const joinConversation = (conversationId: string | number) => {
    if (socketRef.current?.connected) {
      console.log('🔌 [SOCKET] Joining conversation:', conversationId);
      socketRef.current.emit('join-conversation', conversationId);
    }
  };

  const leaveConversation = (conversationId: string | number) => {
    if (socketRef.current?.connected) {
      console.log('🔌 [SOCKET] Leaving conversation:', conversationId);
      socketRef.current.emit('leave-conversation', conversationId);
    }
  };

  return {
    socket: socketRef.current,
    isConnected,
    joinConversation,
    leaveConversation,
  };
};
